import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface BookingCardProps {
  booking: {
    id: string;
    pnr: string;
    destination: string;
    departureDate: string;
    returnDate: string;
    status: 'confirmed' | 'pending' | 'cancelled';
    travelers: number;
    totalAmount: number;
    currency: string;
    image: string;
    alt: string;
    airline: string;
    flightNumber: string;
  };
}

export default function BookingCard({ booking }: BookingCardProps) {
  const statusConfig = {
    confirmed: {
      bg: 'bg-success/10',
      text: 'text-success',
      icon: 'CheckCircleIcon' as const,
      label: 'Confirmed'
    },
    pending: {
      bg: 'bg-warning/10',
      text: 'text-warning',
      icon: 'ClockIcon' as const,
      label: 'Pending'
    },
    cancelled: {
      bg: 'bg-error/10',
      text: 'text-error',
      icon: 'XCircleIcon' as const,
      label: 'Cancelled'
    }
  };

  const config = statusConfig[booking.status];

  return (
    <div className="bg-card rounded-xl shadow-brand hover:shadow-brand-lg transition-all duration-300 overflow-hidden border border-border">
      <div className="relative h-48 overflow-hidden">
        <AppImage
          src={booking.image}
          alt={booking.alt}
          className="w-full h-full object-cover"
        />
        <div className={`absolute top-4 right-4 ${config.bg} ${config.text} px-3 py-1.5 rounded-full flex items-center space-x-1.5 backdrop-blur-sm`}>
          <Icon name={config.icon} size={16} />
          <span className="text-xs font-semibold">{config.label}</span>
        </div>
      </div>

      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-heading font-semibold text-lg text-foreground mb-1">
              {booking.destination}
            </h3>
            <p className="text-sm text-muted-foreground">
              PNR: <span className="font-mono font-semibold text-primary">{booking.pnr}</span>
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-heading font-bold text-primary">
              {booking.currency}{booking.totalAmount.toLocaleString()}
            </p>
            <p className="text-xs text-muted-foreground">{booking.travelers} traveler{booking.travelers > 1 ? 's' : ''}</p>
          </div>
        </div>

        <div className="space-y-3 mb-4">
          <div className="flex items-center space-x-3 text-sm">
            <Icon name="CalendarIcon" size={18} className="text-muted-foreground" />
            <span className="text-foreground">
              {booking.departureDate} - {booking.returnDate}
            </span>
          </div>
          <div className="flex items-center space-x-3 text-sm">
            <Icon name="PaperAirplaneIcon" size={18} className="text-muted-foreground" />
            <span className="text-foreground">
              {booking.airline} • {booking.flightNumber}
            </span>
          </div>
        </div>

        <div className="flex items-center space-x-3 pt-4 border-t border-border">
          <Link
            href={`/booking-management/${booking.id}`}
            className="flex-1 px-4 py-2.5 bg-primary text-primary-foreground text-sm font-heading font-semibold rounded-lg hover:bg-secondary transition-colors duration-300 text-center"
          >
            View Details
          </Link>
          <button className="px-4 py-2.5 border border-border text-foreground text-sm font-medium rounded-lg hover:bg-surface transition-colors duration-300">
            Modify
          </button>
        </div>
      </div>
    </div>
  );
}